import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import nlCommon from './nl/common.json';
import nlCustomers from './nl/customers.json';
import nlDashboard from './nl/dashboard.json';
import nlVehicles from './nl/vehicles.json';
import nlSalesInvoices from './nl/salesInvoices.json';
import nlPurchaseInvoices from './nl/purchaseInvoices.json';
import nlTimeline from './nl/timeline.json';
import nlCalendar from './nl/calendar.json';
import nlPipeline from './nl/pipeline.json';
import nlGarage from './nl/garage.json';
import nlSettings from './nl/settings.json';
import nlNotepad from './nl/notepad.json';
import nlAccounting from './nl/accounting.json';
import nlOnboarding from './nl/onboarding.json';
import nlAdmin from './nl/admin.json';
import nlInventory from './nl/inventory.json';
import enCommon from './en/common.json';
import enCustomers from './en/customers.json';
import enDashboard from './en/dashboard.json';
import enVehicles from './en/vehicles.json';
import enSalesInvoices from './en/salesInvoices.json';
import enPurchaseInvoices from './en/purchaseInvoices.json';
import enTimeline from './en/timeline.json';
import enCalendar from './en/calendar.json';
import enPipeline from './en/pipeline.json';
import enGarage from './en/garage.json';
import enSettings from './en/settings.json';
import enNotepad from './en/notepad.json';
import enAccounting from './en/accounting.json';
import enOnboarding from './en/onboarding.json';
import enAdmin from './en/admin.json';
import enInventory from './en/inventory.json';
// DE/FR: Auth-related translations only (signup, login, onboarding)
import deCommon from './de/common.json';
import frCommon from './fr/common.json';
import { logger } from '@/lib/logger';

// Get stored language from localStorage (for persistence across sessions)
function getStoredLanguage(): 'nl' | 'en' {
  try {
    const stored = localStorage.getItem('modcar-ui-lang');
    if (stored === 'nl' || stored === 'en') {
      return stored;
    }
  } catch {
    // localStorage not available (SSR or private browsing)
  }
  return 'nl'; // Default to Dutch
}

void i18n.use(initReactI18next).init({
  lng: getStoredLanguage(),
  fallbackLng: 'en',
  supportedLngs: ['nl', 'en', 'de', 'fr'],
  ns: [
    'common',
    'customers',
    'dashboard',
    'vehicles',
    'salesInvoices',
    'purchaseInvoices',
    'timeline',
    'calendar',
    'pipeline',
    'garage',
    'settings',
    'notepad',
    'accounting',
    'onboarding',
    'admin',
    'inventory',
  ],
  defaultNS: 'common',
  resources: {
    nl: {
      common: nlCommon,
      customers: nlCustomers,
      dashboard: nlDashboard,
      vehicles: nlVehicles,
      salesInvoices: nlSalesInvoices,
      purchaseInvoices: nlPurchaseInvoices,
      timeline: nlTimeline,
      calendar: nlCalendar,
      pipeline: nlPipeline,
      garage: nlGarage,
      settings: nlSettings,
      notepad: nlNotepad,
      accounting: nlAccounting,
      onboarding: nlOnboarding,
      admin: nlAdmin,
      inventory: nlInventory,
    },
    en: {
      common: enCommon,
      customers: enCustomers,
      dashboard: enDashboard,
      vehicles: enVehicles,
      salesInvoices: enSalesInvoices,
      purchaseInvoices: enPurchaseInvoices,
      timeline: enTimeline,
      calendar: enCalendar,
      pipeline: enPipeline,
      garage: enGarage,
      settings: enSettings,
      notepad: enNotepad,
      accounting: enAccounting,
      onboarding: enOnboarding,
      admin: enAdmin,
      inventory: enInventory,
    },
    // DE/FR: Auth-related translations only, falls back to EN for other namespaces
    de: {
      common: deCommon,
    },
    fr: {
      common: frCommon,
    },
  },
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

if (import.meta.env.DEV) {
  const loggedKeys = new Set<string>();
  i18n.on('missingKey', (lng, ns, key) => {
    const keyId = `${lng}:${ns}:${key}`;
    if (!loggedKeys.has(keyId)) {
      loggedKeys.add(keyId);
      logger.warn(`[i18n] Missing key: ${keyId}`);
    }
  });
}

export default i18n;
